/**
 * Route guard for protected route GROUPS (R-18.179) — wraps the authenticated shell
 * in routes.tsx, not individual leaf routes.
 *
 * The redirect is DECLARATIVE in render via `<Navigate>` (R-18.181): redirecting
 * inside a `useEffect` would render `children` once before the effect fires, flashing
 * protected content. While the startup `/auth/me` probe is in flight we render a
 * spinner so the guard does not bounce to /login before the session is known.
 *
 * An unauthenticated user is sent to `/login` carrying `state.from` (R-18.180) so the
 * login screen can return them to the page they originally requested.
 *
 * Mandatory 2FA (plan R2 §6): when 2FA is mandatory for the user and this session was not opened
 * with a code, the guard renders the 2FA screen INSTEAD of the shell - whatever the URL - so none
 * of the shell's own requests (menu counters, notifications, the page's lists) run into the
 * server's 403 MFA_REQUIRED. Once the enrolment confirms (or the user signs in again with a code)
 * /auth/me says so and the requested page renders.
 */

import { Spin } from 'antd';
import { Suspense, lazy } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { isMfaPending, useAuth } from '@/shared/auth/AuthProvider';

const MfaRequiredPage = lazy(() =>
  import('@/features/auth').then((m) => ({ default: m.MfaRequiredPage })),
);

export function ProtectedRoute(): React.JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <Spin fullscreen />;
  }

  if (!isAuthenticated) {
    // R-18.180/R-18.181 — declarative redirect; `replace` keeps the protected URL out
    // of history, `state.from` lets the login screen redirect back after success.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isMfaPending(user)) {
    return (
      <Suspense fallback={<Spin fullscreen />}>
        <MfaRequiredPage />
      </Suspense>
    );
  }

  return <Outlet />;
}
